import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Hreflang } from "@/i18n/LanguageContext";

export default function PrivacyPolicyNL() {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-policy-page-nl">
      <Helmet>
        <title>Privacybeleid | DevisMaison.be</title>
        <meta name="description" content="Privacybeleid van DevisMaison.be - Hoe wij uw persoonsgegevens verzamelen, gebruiken en beschermen conform de AVG (GDPR)." />
        <link rel="canonical" href="https://devismaison.be/nl/privacybeleid" />
      </Helmet>
      <Hreflang frPath="/politique-de-confidentialite" nlPath="/nl/privacybeleid" />
      <Navbar />

      <main className="pt-24 pb-16 lg:pt-32 lg:pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-8">
            Privacybeleid
          </h1>

          <p className="text-slate-600 mb-8">
            Laatste update: {new Date().toLocaleDateString("nl-BE", { day: "numeric", month: "long", year: "numeric" })}
          </p>

          <div className="prose prose-slate max-w-none">
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                1. Inleiding
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                DevisMaison.be (hierna "wij", "ons" of "het Platform") verbindt zich ertoe de privacy van de gebruikers van zijn website te beschermen. Dit privacybeleid legt uit hoe wij uw persoonsgegevens verzamelen, gebruiken, opslaan en beschermen conform de Algemene Verordening Gegevensbescherming (AVG - Verordening EU 2016/679) en de Belgische wet van 30 juli 2018 betreffende de bescherming van natuurlijke personen met betrekking tot de verwerking van persoonsgegevens.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Door onze website en diensten te gebruiken, aanvaardt u de praktijken beschreven in dit privacybeleid.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                2. Verwerkingsverantwoordelijke
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                De verantwoordelijke voor de verwerking van de persoonsgegevens is:
              </p>
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <p className="text-slate-700 font-medium">Eigenaar van de website: Stéphane Muller (natuurlijke persoon)</p>
                <p className="text-slate-600 mt-3 font-medium">Exploitant: Muller Construction SRL</p>
                <p className="text-slate-600">KBO / Btw-nr.: BE 0803.324.50</p>
                <p className="text-slate-600">Maatschappelijke zetel: Bertrix, België</p>
                <p className="text-slate-600">Telefoon: +32 61 46 83 32</p>
                <p className="text-slate-600">E-mail: contact@devismaison.be</p>
                <p className="text-slate-600">Website: www.devismaison.be</p>
              </div>
              <p className="text-slate-600 leading-relaxed mt-4">
                Stéphane Muller behoudt zich als eigenaar van de website het recht voor om op elk moment van exploitant te veranderen. In dat geval wordt de nieuwe verwerkingsverantwoordelijke aan de gebruikers meegedeeld via een update van dit beleid.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                3. Verzamelde persoonsgegevens
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Wij verzamelen de volgende categorieën persoonsgegevens:
              </p>

              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">
                3.1 Gegevens van particulieren (offerteaanvragers)
              </h3>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                <li>Identificatiegegevens: voornaam, naam</li>
                <li>Contactgegevens: e-mailadres, telefoonnummer</li>
                <li>Locatiegegevens: postcode, gemeente</li>
                <li>Projectgegevens: soort werken, projectbeschrijving, geschat budget, gewenste termijn</li>
              </ul>

              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">
                3.2 Gegevens van aannemers
              </h3>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                <li>Identificatiegegevens van de onderneming: bedrijfsnaam, btw-nummer</li>
                <li>Professionele contactgegevens: e-mailadres, telefoonnummer</li>
                <li>Authenticatiegegevens: wachtwoord (versleuteld opgeslagen)</li>
                <li>Commerciële gegevens: werkgebieden, aangeboden soorten werken</li>
                <li>Betaalgegevens: SEPA-overschrijving (wij bewaren de referentie en het bedrag van de transactie; er worden geen bankkaartgegevens verzameld)</li>
              </ul>

              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">
                3.3 Navigatiegegevens
              </h3>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>IP-adres</li>
                <li>Browsertype en besturingssysteem</li>
                <li>Bezochte pagina's en bezoekduur</li>
                <li>Technische en analytische cookies (zie sectie 8)</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                4. Doeleinden van de verwerking
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Uw persoonsgegevens worden verwerkt voor de volgende doeleinden:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Bemiddeling:</strong> uw offerteaanvragen doorsturen naar de overeenkomstige aannemers</li>
                <li><strong>Beheer van aannemersaccounts:</strong> aanmaak, authenticatie en beheer van professionele accounts</li>
                <li><strong>Betalingsverwerking:</strong> beheer van transacties voor de aankoop van leads</li>
                <li><strong>Communicatie:</strong> verzending van meldingen over uw aanvragen of uw account</li>
                <li><strong>Verbetering van de diensten:</strong> statistische analyse om ons platform te verbeteren</li>
                <li><strong>Wettelijke verplichtingen:</strong> naleving van onze wettelijke en fiscale verplichtingen</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                5. Rechtsgrond van de verwerking
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Conform artikel 6 van de AVG verwerken wij uw gegevens op de volgende rechtsgronden:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Uitvoering van de overeenkomst (art. 6.1.b):</strong> de verwerking is noodzakelijk om onze bemiddelingsdiensten te leveren</li>
                <li><strong>Toestemming (art. 6.1.a):</strong> voor het versturen van marketingcommunicatie (indien van toepassing)</li>
                <li><strong>Gerechtvaardigd belang (art. 6.1.f):</strong> om onze diensten te verbeteren en fraude te voorkomen</li>
                <li><strong>Wettelijke verplichting (art. 6.1.c):</strong> om onze boekhoudkundige en fiscale verplichtingen na te leven</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                6. Ontvangers van de gegevens
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Uw persoonsgegevens kunnen worden gedeeld met:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Partneraannemers:</strong> de contactgegevens van de offerteaanvragers worden doorgegeven aan de aannemers die de lead hebben gekocht</li>
                <li><strong>Dienstverleners:</strong>
                  <ul className="list-circle pl-6 mt-2 space-y-1">
                    <li>Resend (e-mailverzending)</li>
                    <li>MongoDB Atlas (gegevenshosting)</li>
                  </ul>
                </li>
                <li><strong>Bevoegde autoriteiten:</strong> in geval van wettelijke verplichting</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                Wij verkopen uw persoonsgegevens nooit aan derden voor directmarketingdoeleinden.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                7. Internationale doorgiften
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Sommige van onze dienstverleners kunnen gegevens verwerken buiten de Europese Economische Ruimte (EER). In dat geval zorgen wij ervoor dat passende waarborgen aanwezig zijn, met name de standaardcontractbepalingen van de Europese Commissie of de certificering onder het EU-VS-kader voor gegevensbescherming (EU-US Data Privacy Framework).
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                8. Cookies
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Onze website gebruikt cookies om uw ervaring te verbeteren:
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full border border-slate-200 rounded-lg overflow-hidden">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Doel</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Duur</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    <tr>
                      <td className="px-4 py-3 text-sm text-slate-600">Essentiële cookies</td>
                      <td className="px-4 py-3 text-sm text-slate-600">Werking van de website, authenticatie</td>
                      <td className="px-4 py-3 text-sm text-slate-600">Sessie / 7 dagen</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-slate-600">Analytische cookies</td>
                      <td className="px-4 py-3 text-sm text-slate-600">Geanonimiseerde bezoekstatistieken</td>
                      <td className="px-4 py-3 text-sm text-slate-600">13 maanden</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-slate-600 leading-relaxed mt-4">
                U kunt uw cookievoorkeuren beheren via de instellingen van uw browser.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                9. Bewaartermijn
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Wij bewaren uw persoonsgegevens gedurende de volgende termijnen:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Offerteaanvragen:</strong> 3 jaar na de laatste interactie</li>
                <li><strong>Aannemersaccounts:</strong> duur van de commerciële relatie + 5 jaar (boekhoudkundige verplichtingen)</li>
                <li><strong>Betaalgegevens:</strong> 10 jaar (Belgische fiscale verplichtingen)</li>
                <li><strong>Verbindingslogs:</strong> 1 jaar</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                10. Uw rechten
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Conform de AVG en de Belgische wet beschikt u over de volgende rechten:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Recht op inzage (art. 15):</strong> een kopie van uw persoonsgegevens verkrijgen</li>
                <li><strong>Recht op rectificatie (art. 16):</strong> onjuiste of onvolledige gegevens laten corrigeren</li>
                <li><strong>Recht op gegevenswissing (art. 17):</strong> de verwijdering van uw gegevens vragen ("recht op vergetelheid")</li>
                <li><strong>Recht op beperking (art. 18):</strong> de verwerking van uw gegevens beperken</li>
                <li><strong>Recht op overdraagbaarheid (art. 20):</strong> uw gegevens in een gestructureerd formaat ontvangen</li>
                <li><strong>Recht van bezwaar (art. 21):</strong> bezwaar maken tegen de verwerking van uw gegevens</li>
                <li><strong>Recht om uw toestemming in te trekken:</strong> op elk moment, zonder afbreuk te doen aan de rechtmatigheid van de eerdere verwerking</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                Om deze rechten uit te oefenen, contacteer ons via: <a href="mailto:contact@devismaison.be" className="text-orange-500 hover:underline">contact@devismaison.be</a>
              </p>
              <p className="text-slate-600 leading-relaxed mt-2">
                Wij beantwoorden uw aanvraag binnen een maand. Deze termijn kan met twee maanden worden verlengd bij complexe aanvragen.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                11. Gegevensbeveiliging
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Wij nemen passende technische en organisatorische maatregelen om uw gegevens te beschermen:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>SSL/TLS-versleuteling voor alle communicatie</li>
                <li>Wachtwoorden gehasht met het bcrypt-algoritme</li>
                <li>Authenticatie via beveiligde JWT-tokens</li>
                <li>Beperkte toegang tot gegevens op need-to-know-basis</li>
                <li>Regelmatige en beveiligde back-ups</li>
                <li>Monitoring en inbraakdetectie</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                12. Bescherming van minderjarigen
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Onze diensten zijn niet gericht op personen jonger dan 18 jaar. Wij verzamelen niet bewust persoonsgegevens van minderjarigen. Als wij ontdekken dat wij gegevens van een minderjarige hebben verzameld, verwijderen wij deze onmiddellijk.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                13. Recht om klacht in te dienen
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Als u van mening bent dat de verwerking van uw persoonsgegevens een schending van de AVG vormt, heeft u het recht een klacht in te dienen bij de Belgische Gegevensbeschermingsautoriteit:
              </p>
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <p className="text-slate-700 font-medium">Gegevensbeschermingsautoriteit (GBA)</p>
                <p className="text-slate-600">Drukpersstraat 35, 1000 Brussel</p>
                <p className="text-slate-600">Telefoon: +32 2 274 48 00</p>
                <p className="text-slate-600">E-mail: contact@apd-gba.be</p>
                <p className="text-slate-600">Website: <a href="https://www.gegevensbeschermingsautoriteit.be" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">www.gegevensbeschermingsautoriteit.be</a></p>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                14. Wijzigingen van het beleid
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Wij behouden ons het recht voor dit privacybeleid op elk moment te wijzigen. De wijzigingen worden op deze pagina gepubliceerd met een nieuwe updatedatum. Bij substantiële wijzigingen informeren wij u per e-mail of via een melding op onze website.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                15. Contact
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Voor elke vraag over dit privacybeleid of de verwerking van uw persoonsgegevens, contacteer ons:
              </p>
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <p className="text-slate-700 font-medium">Eigenaar van de website: Stéphane Muller (natuurlijke persoon)</p>
                <p className="text-slate-600 mt-3 font-medium">Exploitant: Muller Construction SRL</p>
                <p className="text-slate-600">KBO / Btw-nr.: BE 0803.324.50</p>
                <p className="text-slate-600">E-mail: <a href="mailto:contact@devismaison.be" className="text-orange-500 hover:underline">contact@devismaison.be</a></p>
                <p className="text-slate-600">Telefoon: +32 61 46 83 32</p>
                <p className="text-slate-600">Adres: Bertrix, België</p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
